import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, TrendingDown, Loader2 } from "lucide-react";
import { apiClient } from "@/api/client";
import { formatApiError } from "@/utils/httpErrors";

interface DebtLikeItem {
  category: string;
  description: string;
  amount: number;
  source: string;
  confidence: string;
  note?: string;
}

interface EquityBridgeData {
  // FIELDS-1 P1b: null when no enterprise value has been entered yet. The
  // endpoint returns an honest-empty 200 in that state rather than a 422, so
  // these must be nullable — formatCurrency(null) would print "$0" and pass a
  // deal with no valuation off as a bridge that nets to zero.
  enterprise_value: number | null;
  enterprise_value_source?: string;
  debt_like_items: DebtLikeItem[];
  total_debt_like: number;
  // Signed: positive = working-capital surplus above the peg, negative = shortfall
  // below it. Not excess cash — see core/qoe/debt_like_items.py.
  working_capital_true_up: number;
  equity_value: number | null;
  equity_value_range_low: number | null;
  equity_value_range_high: number | null;
  items_confirmed: number;
  items_estimated: number;
  items_missing: string[];
  note: string;
}

interface EquityBridgeCardProps {
  dealId?: string;
  /** Bumped by the equity-bridge intake form after a save, to re-read. */
  refreshKey?: number;
}

const formatCurrency = (val: number | null) => {
  // FIELDS-1 P1b: an em dash, never "$0". Intl.format(null) renders "$0", which
  // would state a figure the deal does not have.
  if (val === null || val === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
};

export const EquityBridgeCard: React.FC<EquityBridgeCardProps> = ({ dealId, refreshKey = 0 }) => {
  const [data, setData] = useState<EquityBridgeData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!dealId) return;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get<EquityBridgeData>(`/qoe/deals/${dealId}/equity-bridge`);
        if (!cancelled) setData(res.data);
      } catch (err: any) {
        if (!cancelled) {
          if (err.response?.status === 404) {
            // FIELDS-1 P1b: a 404 now means the deal itself is missing. The old
            // copy blamed the executive summary, which is no longer where the
            // enterprise value has to come from.
            setError("Deal not found — the equity bridge could not be loaded.");
          } else if (err.response?.status === 422) {
            setError(formatApiError(err));
          } else {
            setError("Failed to load equity bridge data");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [dealId, refreshKey]);

  if (!dealId) return null;

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="ml-2">Loading equity bridge...</span>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="bg-amber-50 border-l-4 border-amber-400">
        <CardContent className="p-4">
          <div className="flex gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">{error || "No equity bridge data available"}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // FIELDS-1 P1b: no enterprise value captured yet. The endpoint answers 200
  // with nulls rather than 422 — an unstarted deal is not an error — so this is
  // a calm, muted "what to do next", not the amber failure card above.
  if (data.enterprise_value === null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingDown className="h-5 w-5" />
            Equity Value Bridge
          </CardTitle>
          <CardDescription>EV → Equity Value (debt-like items adjustment)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">{data.note}</p>
          {data.items_missing.length > 0 && (
            <ul className="text-xs text-muted-foreground space-y-1">
              {data.items_missing.map((item, idx) => (
                <li key={idx} className="flex gap-2">
                  <span>•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingDown className="h-5 w-5" />
          Equity Value Bridge
        </CardTitle>
        <CardDescription>EV → Equity Value (debt-like items adjustment)</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Main bridge display */}
        <div className="space-y-3 bg-gray-50 p-4 rounded border border-gray-200 text-sm">
          <div className="flex justify-between">
            <span>
              Enterprise Value
              {/* FIELDS-1 P1b: the captured EV and the engagement sizing proxy on
                  the executive summary are different numbers with different
                  purposes. Which one this bridge is built on has to be visible. */}
              {data.enterprise_value_source && (
                <span className="text-xs text-gray-500 ml-2">({data.enterprise_value_source})</span>
              )}
            </span>
            <span className="font-semibold tabular-nums">{formatCurrency(data.enterprise_value)}</span>
          </div>

          {data.debt_like_items.length > 0 && (
            <>
              <div className="border-t pt-2 mt-2">
                <div className="text-gray-600 mb-2">Less: Debt-Like Items</div>
                {data.debt_like_items.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-xs text-gray-700 ml-2 mb-1">
                    <span>{item.description}</span>
                    <span className="tabular-nums">({formatCurrency(item.amount)})</span>
                  </div>
                ))}
              </div>

              <div className="flex justify-between text-gray-700 pt-2">
                <span>Total Debt-Like Items</span>
                <span className="font-semibold tabular-nums">({formatCurrency(data.total_debt_like)})</span>
              </div>
            </>
          )}

          {/* !== 0, not > 0: a shortfall is exactly the case the old `> 0` test
              hid, and hiding it while it reduces equity below made the bridge
              fail to foot. Shortfalls render in parentheses like the debt-like
              deductions above, because that is what they are. */}
          {data.working_capital_true_up !== 0 && (
            <div
              className={`flex justify-between pt-2 ${
                data.working_capital_true_up > 0 ? "text-green-700" : "text-red-700"
              }`}
            >
              <span>Working-capital true-up vs peg (+ surplus / − shortfall)</span>
              <span className="font-semibold tabular-nums">
                {data.working_capital_true_up > 0
                  ? formatCurrency(data.working_capital_true_up)
                  : `(${formatCurrency(Math.abs(data.working_capital_true_up))})`}
              </span>
            </div>
          )}

          <div className="border-t-2 border-accent pt-3 mt-3 flex justify-between font-bold text-base bg-muted/60 -mx-4 -mb-4 px-4 pb-4 rounded-b">
            <span>Estimated Equity Value</span>
            <span className="tabular-nums text-accent-foreground">{formatCurrency(data.equity_value)}</span>
          </div>
        </div>

        {/* Range */}
        {(data.equity_value_range_low !== data.equity_value_range_high) && (
          <div className="text-sm text-gray-700">
            <strong>Range:</strong> {formatCurrency(data.equity_value_range_low)} – {formatCurrency(data.equity_value_range_high)}
          </div>
        )}

        {/* Confidence summary */}
        {(data.items_estimated > 0 || data.items_missing.length > 0) && (
          <div className="space-y-2 bg-muted border border-accent/40 rounded p-3">
            {data.items_estimated > 0 && (
              <div className="flex gap-2">
                <AlertCircle className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                <p className="text-xs text-accent-foreground">
                  {data.items_estimated} item{data.items_estimated > 1 ? "s" : ""} estimated (not confirmed). Request supporting documents.
                </p>
              </div>
            )}

            {data.items_missing.length > 0 && (
              <div className="text-xs text-accent-foreground space-y-1">
                <div className="font-semibold">Missing items:</div>
                {data.items_missing.map((item, idx) => (
                  <div key={idx} className="flex gap-2">
                    <span className="text-accent">•</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer note */}
        <div className="text-xs text-gray-600 italic">{data.note}</div>
      </CardContent>
    </Card>
  );
};
